import { eventChannel } from 'redux-saga';
import { call, put, take, takeLatest } from 'redux-saga/effects';
import type { Participant } from '@nx/domain';
import { participantRepository } from '../../services/eoRepositories';
import { pushToast } from '../slices/eoUiSlice';
import {
  participantCheckInRequested,
  participantCreateRequested,
  participantImportRequested,
  participantUpdateRequested,
  participantsFailed,
  participantsLoaded,
  participantsSaveSucceeded,
  participantsWatchRequested,
} from '../slices/participantsSlice';

function* watchParticipants(eventId: string): Generator<unknown, void, unknown> {
  const channel = eventChannel<Participant[]>((emit) =>
    participantRepository.watchByEvent(eventId, (items) => emit(items)),
  );
  try {
    while (true) {
      yield put(participantsLoaded((yield take(channel)) as Participant[]));
    }
  } finally {
    channel.close();
  }
}

export function* handleWatch(action: ReturnType<typeof participantsWatchRequested>): Generator<unknown, void, unknown> {
  yield call(watchParticipants, action.payload);
}

export function* handleCreate(action: ReturnType<typeof participantCreateRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [participantRepository, participantRepository.create],
      action.payload.eventId,
      action.payload.input,
    );
    yield put(participantsSaveSucceeded());
    yield put(pushToast({ msg: 'eo.toast.participantAdded', kind: 'success' }));
  } catch (e) {
    yield put(participantsFailed(e instanceof Error ? e.message : 'Failed'));
    yield put(pushToast({ msg: 'eo.toast.participantAddFailed', kind: 'danger' }));
  }
}

function* handleUpdate(action: ReturnType<typeof participantUpdateRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [participantRepository, participantRepository.update],
      action.payload.eventId,
      action.payload.participantId,
      action.payload.input,
    );
    yield put(participantsSaveSucceeded());
  } catch (e) {
    yield put(participantsFailed(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleImport(action: ReturnType<typeof participantImportRequested>): Generator<unknown, void, unknown> {
  try {
    const count = (yield call(
      [participantRepository, participantRepository.batchCreate],
      action.payload.eventId,
      action.payload.inputs,
    )) as number;
    yield put(
      pushToast({
        msg: 'eo.toast.participantsImported',
        params: { count: String(count) },
        kind: 'success',
      }),
    );
  } catch (e) {
    yield put(pushToast({ msg: 'eo.toast.participantImportFailed', kind: 'danger' }));
  }
}

function* handleCheckIn(action: ReturnType<typeof participantCheckInRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [participantRepository, participantRepository.update],
      action.payload.eventId,
      action.payload.participantId,
      { checkedIn: true, status: 'confirmed' },
    );
    yield put(pushToast({ msg: 'eo.toast.participantCheckedIn', kind: 'success' }));
  } catch (e) {
    yield put(pushToast({ msg: 'eo.toast.participantCheckInFailed', kind: 'danger' }));
  }
}

export function* participantsSaga() {
  yield takeLatest(participantsWatchRequested.type, handleWatch);
  yield takeLatest(participantCreateRequested.type, handleCreate);
  yield takeLatest(participantUpdateRequested.type, handleUpdate);
  yield takeLatest(participantImportRequested.type, handleImport);
  yield takeLatest(participantCheckInRequested.type, handleCheckIn);
}
